import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';
import { PHONE_PATTERN } from '../profile-constants';
import type { User } from '../../../../shared/UserInterface';
import type { ProfileUpdateRequest } from '../../../../models/profile.interface';

interface ContactFormShape {
  contactEmail: string;
  mobilePhone: string;
}

@Component({
  selector: 'app-contact-info',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent],
  templateUrl: './contact-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../_collapsible-card.css'],
})
export class ContactInfoComponent {
  @Input({ required: true }) set user(value: User | null | undefined) {
    this._user = value;
    if (!value) return;
    // Don't clobber in-progress edits when the parent's currentUser signal
    // refreshes (e.g. after a sibling section saves). Collapsed cards always
    // re-sync; the saving card accepts the fresh payload via `isSaving()`.
    if (this.isExpanded() && this.form.dirty && !this.isSaving()) return;
    this.populateForm(value);
  }
  get user(): User | null | undefined {
    return this._user;
  }
  private _user: User | null | undefined;

  @Output() save = new EventEmitter<Partial<ProfileUpdateRequest>>();

  private fb = inject(FormBuilder);

  readonly IconType = IconType;

  isExpanded = signal(false);
  isSaving = signal(false);

  form: FormGroup = this.fb.group({
    contactEmail: ['', [Validators.email]],
    mobilePhone: ['', [Validators.pattern(PHONE_PATTERN)]],
  });

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (this.isExpanded() && this._user) {
      this.populateForm(this._user);
    }
  }

  onSubmit(): void {
    if (!this.form.valid || this.isSaving()) return;
    const diff = this.collectDiff();
    if (Object.keys(diff).length === 0) {
      this.isExpanded.set(false);
      return;
    }
    this.isSaving.set(true);
    this.save.emit(diff);
  }

  onSaveComplete(success: boolean): void {
    this.isSaving.set(false);
    if (success) {
      this.isExpanded.set(false);
    }
  }

  hasError(field: keyof ContactFormShape, error: string): boolean {
    const control = this.form.get(field);
    return !!(control?.touched && control?.hasError(error));
  }

  hasChanges(): boolean {
    return Object.keys(this.collectDiff()).length > 0;
  }

  isFieldChanged(field: keyof ContactFormShape): boolean {
    return field in this.collectDiff();
  }

  private populateForm(user: User): void {
    this.form.reset({
      contactEmail: user.contactEmail ?? '',
      mobilePhone: user.mobilePhone ?? '',
    });
  }

  private collectDiff(): Partial<ProfileUpdateRequest> {
    const value = this.form.value as ContactFormShape;
    const diff: Partial<ProfileUpdateRequest> = {};
    const u = this._user;
    if (!u) return diff;

    if (value.contactEmail !== (u.contactEmail ?? '')) {
      diff.contactEmail = value.contactEmail;
    }
    if (value.mobilePhone !== (u.mobilePhone ?? '')) {
      diff.mobilePhone = value.mobilePhone;
    }
    return diff;
  }
}
